<template>
    <div class="dailyOffer">
        <div class="row d-flex align-items-center justify-content-center">
            <div v-if="product && product.url" class="col-12 col-lg-6">
                <div class="daily-offer">
                    <nuxt-link :to="product.url" class="daily-offer__thumb">
                        <span><img :src="product.imageCdn" :alt="product.name" /></span>
                        <div v-if="product.percentDiscount" class="discount">-{{ product.percentDiscount }} %</div>
                    </nuxt-link>
                    <div class="daily-offer__body">
                        <div class="daily-offer__link">
                            <nuxt-link :to="product.url" class="title title--top">{{ $t('dailyOffer.title') }}</nuxt-link>
                            <nuxt-link :to="product.url" class="name">{{ product.name }}</nuxt-link>
                            <ElementsPriceInMobileApp v-if="product.mobileAppPrice" :product="transformProductToFullProduct(product)" />
                            <nuxt-link :to="product.url" class="daily-offer__link">
                                <div class="price">
                                    <span
                                        v-if="product.priceBeforeDiscount !== null && product.priceBeforeDiscount > product.price"
                                        class="old notranslate"
                                        v-html="$formatCurrency(product.priceBeforeDiscount)"
                                    ></span>
                                    <span class="notranslate" v-html="$formatCurrency(product.price)"></span>
                                </div>
                            </nuxt-link>
                            <div class="addtocart">
                                <ElementsButtonAddBasket
                                    :product-id="product.id"
                                    :product-price="product.price"
                                    :is-preorder="product.isPreorder"
                                    :max-amount-per-order="product.attributes.maxAmountPerOrder"
                                    :has-substitution-modal="product.substitutions?.isModal || false"
                                    :item-list-name="dailyDealItemListName"
                                    button-class="btn-primary w-100"
                                />
                            </div>
                        </div>
                        <div class="daily-offer-bottom">
                            <div class="d-flex mb-3 daily-offer__box">
                                <div class="countdown-wrapper">
                                    <div class="title">{{ $t('dailyOffer.end') }}:</div>
                                    <Countdown />
                                </div>
                                <div class="stock">
                                    <div class="title">{{ $t('dailyOffer.pieces') }}:</div>
                                    <div class="value">
                                        <span class="numbers" v-html="formatStock(product.amountInStock)"></span>
                                        <span class="stock">{{ $t('pieces') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="dd-banner col-12 col-lg-6 d-flex justify-content-center">
                <nuxt-link class="dd-banner__link" :to="$t('ddBanner.link')">
                    <picture v-lazy-load>
                        <source
                            media="(min-width: 480px)"
                            :srcset="`/nuxt-assets/images/homepage/app/` + $t('locale') + `/dd-banner.jpg`"
                        />
                        <img
                            :src="`/nuxt-assets/images/homepage/app/` + $t('locale') + `/dd-banner-sm.jpg`"
                            :alt="`Každý den nové slevy`"
                        />
                    </picture>

                    <div class="dd-banner__content text-black" href="#">
                        <div class="title h2 mb-2 mb-sm-3">{{ $t('ddBanner.title') }}</div>
                        <div class="desc mb-3 mb-sm-4">{{ $t('ddBanner.desc') }}</div>
                        <a :href="$t('ddBanner.link')" class="btn btn-primary px-4">{{ $t('ddBanner.btnText') }}</a>
                    </div>
                </nuxt-link>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
import { PropType } from 'vue'
import Countdown from '~/components/elements/Countdown.vue'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import Product from '~/models/Product/Product'
import { transformProductToFullProduct } from '~/utils/transformProductToFullProduct'

defineProps({
    product: {
        type: Object as PropType<Product>,
        default: () => ({}),
    },
})

const dailyDealItemListName = computed((): string => {
    return GtmItemListNameEnum.HPHitOfTheWeek
})

const formatStock = (stock: number) => {
    if (stock > 10) {
        return '10+'
    }

    return stock
}
</script>

<style lang="scss" scoped>
.dailyOffer {
    max-width: 1280px;
    margin: 0 auto 2em auto;
}
</style>

<style lang="scss" scoped>
.dd-banner {
    position: relative;
    padding: 2em 0;

    @include screen-md {
        padding: 0;
    }

    &__link {
        position: relative;
        display: block;
        overflow: hidden;

        @include screen-sm {
            border-radius: 10px;
        }
    }

    .title {
        font-size: 20px;

        @include screen-sm {
            font-size: 28px;
        }
    }

    .desc {
        font-size: 12px;

        @include screen-sm {
            font-size: 14px;
        }
    }

    @media (max-width: 767px) {
        .btn {
            font-size: 14px;
            height: 31px;
            padding: 0;
            line-height: 28px;
        }
    }

    &__content {
        position: absolute;
        top: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 55%;
        align-items: baseline;
        padding: 1em 5% 1em 1em;

        @media (min-width: 768px) {
            align-items: center;
            width: 100%;
        }
    }
}

.daily-offer {
    position: relative;
    display: grid;
    gap: 1rem;

    @include screen-md() {
        display: flex;
        gap: 1em;
        align-items: center;
    }

    &__thumb {
        display: flex;
        justify-content: flex-end;
        position: relative;
        width: 100%;
        height: 100%;
        max-width: 400px;
        max-height: 303px;
        color: $color-dark;

        @media (max-width: 767px) {
            flex: 0 0 45%;
        }

        img {
            max-height: 260px;
        }

        > span {
            border-radius: 41% 59% 56% 44% / 34% 53% 47% 66%;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            max-width: 340px;
            min-height: 280px;
            background-color: #fff;

            @media (max-width: 768px) {
                min-height: auto;
            }
        }

        &::before {
            content: '';
            position: absolute;
            z-index: 1;
            left: 0;
            margin: auto;
            top: 0;
            width: 100%;
            height: 100%;
            background: url('/nuxt-assets/images/png/bg-product.png') no-repeat center center;
            background-size: contain;

            @media (min-width: 768px) {
                right: -20px;
            }
        }

        .discount {
            right: 26px;
            top: 8px;
        }
    }

    &__body {
        text-align: left;

        @media (min-width: 768px) {
            padding-left: 2em;
        }

        .title--top {
            position: static;
            top: -2em;
            font-size: 1.6rem;
            margin-bottom: 0.5em;
            font-weight: 500;
            color: $color-dark;

            @media (max-width: 767px) {
                position: absolute;
                bottom: 100%;
                left: 0;
                padding: 0 0 0 3px;
                font-size: 1.2rem;
                margin-bottom: 0.25em;
            }
        }
    }

    &__link {
        text-decoration: none;
        color: inherit;
        display: flex;
        flex-direction: column;

        &:hover {
            text-decoration: none;
            color: inherit;
        }
    }

    &__box {
        @media (min-width: 768px) {
            align-items: center;
        }
    }

    .name {
        margin-bottom: 1em;
        font-weight: 500;
        color: $color-dark;

        @media (max-width: 767px) {
            margin-bottom: 1em;
        }
    }

    .countdown-wrapper,
    .stock {
        .title {
            display: block;
            margin-right: 0;

            @media (min-width: 768px) {
                margin-right: 1em;
                display: inline-block;
            }
        }
    }

    .countdown-wrapper {
        @media (min-width: 768px) {
            margin-right: 2em;
        }
    }

    .countdown,
    .stock .value {
        margin-top: 10px;
        margin-bottom: 0.3em;
        font-size: 18px;
        color: hsl(0, 58%, 60%);
        white-space: nowrap;
        display: inline-block;
        font-weight: 500;
        margin-right: 1em;

        @include screen-sm {
            margin-right: -15px;
        }
    }

    .stock .value {
        margin-right: 5px;

        .numbers span {
            border-radius: 10px;
            text-align: center;
            width: 25px;
            height: 33px;
            line-height: 33px;
            position: relative;
            display: inline-block;
            z-index: 1;
            background-color: $color-grey;
            margin-left: 3px;
        }

        .stock {
            margin-left: 0.5em;
        }
    }

    .price {
        font-size: 18px;
        margin-bottom: 1em;
        font-weight: 500;

        .old {
            text-decoration: line-through;
            font-size: 18px;
            margin-right: 1em;
            font-weight: normal;

            @media (max-width: 767px) {
                font-size: 14px;
                display: block;
                margin-right: 0;
            }
        }
    }

    .addtocart {
        max-width: 160px;
        margin-bottom: 1em;

        .add-cart__change {
            height: 39px;
        }

        .add-cart__change.btn-minus {
            flex: 0 0 39px;
        }

        .text-end {
            text-align: left !important;
        }
    }

    &-bottom {
        @media (max-width: 767px) {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        > div {
            gap: 2em;
        }
    }

    .alert-addcart {
        color: $color-dark;
        font-size: 14px;
        background-color: $color-green;
        border-radius: 10px;
        padding: 0.5em 1em;
        font-weight: 500;
    }
}
</style>
